import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { toast } from 'sonner';
import { clientApi } from '@/config/axios';
import { useOrganization } from '@/features/members/api/organization-api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Field, FieldGroup, FieldLabel } from '@/components/ui/field';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';

type Branding = {
  clinic_name: string;
  clinic_details: string;
  logo_url: string | null;
  mappings: Record<string, string>;
  templates: { id: string; name: string; renderable: boolean }[];
};
export function ReportBranding() {
  const { data: org } = useOrganization();
  const base = `/api/v1/organizations/${org?.id}/report-branding`;
  const { data, refetch, isError } = useQuery<Branding>({
    queryKey: ['report-branding', org?.id],
    enabled: !!org,
    queryFn: async () => (await clientApi.get(base)).data,
  });
  const {
    data: signatureProfile,
    refetch: refetchSignature,
    isError: signatureError,
  } = useQuery<{ text: string }>({
    queryKey: ['my-signature-text', org?.id],
    enabled: !!org,
    queryFn: async () => (await clientApi.get('/api/v1/auth/signature-text')).data,
  });
  const [signatureText, setSignatureText] = useState('');
  useEffect(() => {
    if (signatureProfile) setSignatureText(signatureProfile.text);
  }, [signatureProfile]);
  const [name, setName] = useState(''),
    [details, setDetails] = useState(''),
    [busy, setBusy] = useState(false);
  useEffect(() => {
    if (data) {
      setName(data.clinic_name);
      setDetails(data.clinic_details);
    }
  }, [data]);
  async function run(action: () => Promise<unknown>) {
    setBusy(true);
    try {
      await action();
      await refetch();
      toast.success('Report settings updated');
    } catch {
      toast.error('Unable to update report settings');
    } finally {
      setBusy(false);
    }
  }
  async function upload(file: File | undefined, signature = false) {
    if (!file) return;
    const body = new FormData();
    body.append('file', file);
    await run(() => clientApi.post(signature ? '/api/v1/auth/signature' : `${base}/logo`, body));
  }
  return (
    <Card>
      <CardHeader>
        <CardTitle>Report branding and templates</CardTitle>
        <CardDescription>Used for newly generated copies. Previously issued PDFs remain unchanged.</CardDescription>
      </CardHeader>
      <CardContent>
        <FieldGroup>
          {isError && <p role="alert">Unable to load report settings.</p>}
          <Field>
            <FieldLabel htmlFor="report-clinic-name">Clinic name on reports</FieldLabel>
            <Input
              id="report-clinic-name"
              value={name}
              placeholder={org?.name}
              maxLength={200}
              onChange={(e) => setName(e.target.value)}
            />
          </Field>
          <Field>
            <FieldLabel htmlFor="report-clinic-details">Clinic address and contact information</FieldLabel>
            <Textarea
              id="report-clinic-details"
              value={details}
              maxLength={2000}
              onChange={(e) => setDetails(e.target.value)}
            />
          </Field>
          <Button
            type="button"
            disabled={busy || !data}
            onClick={() =>
              run(() =>
                clientApi.put(base, { clinic_name: name, clinic_details: details, mappings: data?.mappings || {} }),
              )
            }>
            Save report settings
          </Button>
          <Field>
            <FieldLabel htmlFor="report-logo">Clinic logo (PNG or JPEG, up to 2 MB)</FieldLabel>
            <Input
              id="report-logo"
              type="file"
              accept="image/png,image/jpeg"
              disabled={busy}
              onChange={(e) => upload(e.target.files?.[0])}
            />
          </Field>
          {data?.logo_url && (
            <>
              <img src={data.logo_url} alt="Clinic report logo" className="max-h-32 max-w-64 object-contain" />
              <Button
                type="button"
                variant="outline"
                disabled={busy}
                onClick={() => run(() => clientApi.delete(`${base}/logo`))}>
                Remove logo
              </Button>
            </>
          )}
          <Field>
            <FieldLabel htmlFor="report-signature">My signature image (PNG or JPEG, up to 2 MB)</FieldLabel>
            <Input
              id="report-signature"
              type="file"
              accept="image/png,image/jpeg"
              disabled={busy}
              onChange={(e) => upload(e.target.files?.[0], true)}
            />
            <p className="text-sm text-muted-foreground">
              Updates your personal signing image. A report still requires your explicit review and signature. Drafts
              never include a signature image.
            </p>
          </Field>
          <Field>
            <FieldLabel htmlFor="report-signature-text">My signature text</FieldLabel>
            <Textarea
              id="report-signature-text"
              rows={3}
              className="min-h-24 max-h-64 overflow-y-auto"
              value={signatureText}
              maxLength={2000}
              disabled={busy || !signatureProfile}
              onChange={(e) => setSignatureText(e.target.value)}
            />
            {signatureError && <p role="alert">Unable to load your signature text.</p>}
            <p className="text-sm text-muted-foreground">
              Appears with your signature on newly signed reports. Previously signed reports retain their original text.
            </p>
            <Button
              type="button"
              disabled={busy || !signatureProfile}
              onClick={() =>
                run(async () => {
                  await clientApi.put('/api/v1/auth/signature-text', { text: signatureText });
                  await refetchSignature();
                })
              }>
              Save signature text
            </Button>
          </Field>
          <Field>
            <FieldLabel>Approved PDF layouts</FieldLabel>
            {data?.templates.map((t) => (
              <p key={t.id}>
                {t.name} — {t.id}
                {t.renderable ? ' (default for clinical reports)' : ' (separate document workflow)'}
              </p>
            ))}
            <p className="text-sm text-muted-foreground">
              Layout templates are managed separately from AI writing templates. New layouts are reviewed and deployed
              by your developer.
            </p>
          </Field>
        </FieldGroup>
      </CardContent>
    </Card>
  );
}
